import { 
  apiService, 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest,
  WebAuthnRegisterStartRequest,
  WebAuthnRegisterFinishRequest,
  WebAuthnLoginStartRequest,
  WebAuthnLoginFinishRequest
} from './api';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';

export interface User {
  id: number;
  username: string;
  name?: string;
  nickname: string;
  email: string;
  role: string;
  status: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  lastLoginAt: string;
}

// /auth/me 중복 호출 방지:
// 개발 모드 React StrictMode에서 초기화 useEffect가 2번 실행될 때
// 같은 시점의 네트워크 요청이 2번 찍히는 것을 in-flight + TTL로 묶는다.
let fetchCurrentUserInFlight: Promise<User | null> | null = null;
let fetchCurrentUserCached: User | null = null;
let fetchCurrentUserCachedAt = 0;
const FETCH_CURRENT_USER_TTL_MS = 3000;

class AuthService {
  private currentUser: User | null = null;

  constructor() {
    this.initializeAuth();
  }

  // Base64 URL 디코딩 (한글 지원)
  private base64UrlDecode(str: string): string {
    // Base64 URL을 일반 Base64로 변환
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // 패딩 추가
    while (base64.length % 4) {
      base64 += '=';
    }
    // Base64 디코딩
    const binaryString = atob(base64);
    // UTF-8 디코딩
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  }

  // 인증 상태 초기화
  // 쿠키 기반이므로 초기화 시에는 user 정보가 없음
  // 로그인 성공 시 user 정보를 저장하여 사용
  private initializeAuth() {
    // 쿠키에서 토큰을 읽을 수 없으므로 초기화 시에는 user 정보 없음
    // 로그인 성공 시 받은 user 정보를 사용
    this.currentUser = null;
  }

  // 로그인
  async login(credentials: LoginRequest): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      const response = await apiService.login({
        ...credentials,
        deviceInfo: navigator.userAgent
      });

      if (response.success && response.data) {
        // API 응답에서 user 정보 추출
        const userData: any = response.data.user;
        if (userData) {
          // User 객체로 변환
          const user: User = {
            id: userData.id || (userData as any).userId || 0,
            username: userData.username,
            name: userData.name,
            nickname: userData.nickname || userData.username,
            email: userData.email || '',
            role: userData.role || 'USER',
            status: userData.status || 'ACTIVE',
            emailVerified: userData.emailVerified || false,
            phoneVerified: userData.phoneVerified || false,
            lastLoginAt: userData.lastLoginAt || new Date().toISOString()
          };
          this.currentUser = user;
          return { success: true, user };
        } else {
          // user 정보가 없으면 에러 반환
          return { success: false, error: '사용자 정보를 가져올 수 없습니다.' };
        }
      } else {
        return { success: false, error: getUnifiedApiErrorMessage(response) };
      }
    } catch (error: unknown) {
      console.error('로그인 오류:', error);
      return {
        success: false,
        error: getApiFailureMessage(error, '로그인 중 오류가 발생했습니다.')
      };
    }
  }

  // 회원가입
  async register(userData: RegisterRequest): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      console.log('회원가입 요청 데이터:', userData);
      const response = await apiService.register(userData);

      if (response.success) {
        return { success: true, message: response.message || '회원가입이 완료되었습니다.' };
      } else {
        console.error('회원가입 응답 실패:', response);
        return { success: false, error: getUnifiedApiErrorMessage(response) };
      }
    } catch (error: unknown) {
      const anyErr = error as { message?: string; response?: { status?: number; statusText?: string; data?: unknown }; config?: unknown };
      console.error('회원가입 오류 상세:', {
        message: anyErr.message,
        status: anyErr.response?.status,
        statusText: anyErr.response?.statusText,
        data: anyErr.response?.data,
        config: anyErr.config
      });
      return {
        success: false,
        error: getApiFailureMessage(error, '회원가입 중 오류가 발생했습니다.')
      };
    }
  }

  // 로그아웃
  async logout(): Promise<void> {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('로그아웃 오류:', error);
    } finally {
      this.currentUser = null;
    }
  }

  // 토큰 갱신
  async refreshToken(): Promise<boolean> {
    try {
      const response = await apiService.refreshToken();
      if (response.success && response.data) {
        this.currentUser = response.data.user;
        return true;
      }
      return false;
    } catch (error) {
      console.error('토큰 갱신 오류:', error);
      this.logout();
      return false;
    }
  }

  // 현재 사용자 정보 반환
  getCurrentUser(): User | null {
    // currentUser가 있으면 반환
    // 쿠키 기반이므로 토큰에서 추출 불가, 로그인 시 받은 user 정보만 사용
    return this.currentUser;
  }

  // 서버에서 현재 사용자 정보 가져오기
  async fetchCurrentUser(): Promise<User | null> {
    try {
      const now = Date.now();
      // StrictMode(개발)에서 초기화 useEffect가 중복 실행되어 /auth/me 요청이 2번 찍힐 수 있다.
      // 같은 시점의 중복 네트워크를 in-flight + TTL 캐시로 차단한다.
      if (fetchCurrentUserCached && now - fetchCurrentUserCachedAt < FETCH_CURRENT_USER_TTL_MS) {
        this.currentUser = fetchCurrentUserCached;
        return fetchCurrentUserCached;
      }

      if (fetchCurrentUserInFlight) {
        const cached = await fetchCurrentUserInFlight;
        this.currentUser = cached;
        return cached;
      }

      fetchCurrentUserInFlight = (async () => {
        const response = await apiService.getCurrentUser();
      if (response.success && response.data) {
        const userData: any = response.data;
        const user: User = {
          id: userData.id || 0,
          username: userData.username,
          name: userData.name,
          nickname: userData.nickname || userData.username,
          email: userData.email || '',
          role: userData.role || 'USER',
          status: userData.status || 'ACTIVE',
          emailVerified: userData.emailVerified || false,
          phoneVerified: userData.phoneVerified || false,
          lastLoginAt: userData.lastLoginAt || new Date().toISOString()
        };
        this.currentUser = user;
        return user;
      }
        return null;
      })();

      const result = await fetchCurrentUserInFlight;
      fetchCurrentUserCached = result;
      fetchCurrentUserCachedAt = Date.now();
      fetchCurrentUserInFlight = null;

      this.currentUser = result;
      return result;
    } catch (error) {
      console.error('사용자 정보 가져오기 실패:', error);
      this.currentUser = null;
      fetchCurrentUserCached = null;
      fetchCurrentUserInFlight = null;
      return null;
    }
  }

  // 인증 상태 확인
  // 쿠키 기반이므로 currentUser가 있으면 인증된 것으로 간주
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // 사용자 역할 확인
  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }

  // 관리자 권한 확인
  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  // 사용자 상태 확인
  isActive(): boolean {
    return this.currentUser?.status === 'ACTIVE';
  }

  // 이메일 인증 상태 확인
  isEmailVerified(): boolean {
    return this.currentUser?.emailVerified === true;
  }

  // 전화번호 인증 상태 확인
  isPhoneVerified(): boolean {
    return this.currentUser?.phoneVerified === true;
  }

  // 사용자 정보 업데이트 (로컬)
  updateUser(user: User): void {
    this.currentUser = user;
  }

  // 사용자 정보 초기화
  clearUser(): void {
    this.currentUser = null;
  }

  // WebAuthn 회원가입
  async webauthnRegister(userData: {
    username: string;
    email: string;
    name: string;
    nickname: string;
    phone?: string;
    password?: string;
    deviceName?: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      // 1. 회원가입 시작 (Challenge 받기)
      const startResponse = await apiService.webauthnRegisterStart({
        username: userData.username,
        email: userData.email,
        name: userData.name,
        nickname: userData.nickname,
        phone: userData.phone,
        password: userData.password,
        deviceName: userData.deviceName || this.getDeviceName()
      });

      if (!startResponse.success || !startResponse.data) {
        return {
          success: false,
          error: getUnifiedApiErrorMessage(startResponse)
        };
      }

      const challengeData = startResponse.data;

      // 2. 브라우저에서 Credential 생성
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: this.base64UrlToArrayBuffer(challengeData.challenge),
        rp: {
          id: challengeData.rp.id,
          name: challengeData.rp.name
        },
        user: {
          id: this.base64UrlToArrayBuffer(challengeData.user.id),
          name: challengeData.user.name,
          displayName: challengeData.user.displayName
        },
        pubKeyCredParams: challengeData.pubKeyCredParams.map(param => ({
          type: param.type as PublicKeyCredentialType,
          alg: param.alg
        })),
        timeout: challengeData.timeout,
        attestation: challengeData.attestation as AttestationConveyancePreference,
        authenticatorSelection: {
          userVerification: challengeData.authenticatorSelection.userVerification as UserVerificationRequirement,
          residentKey: challengeData.authenticatorSelection.residentKey as ResidentKeyRequirement
        }
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      }) as PublicKeyCredential | null;

      if (!credential || !(credential instanceof PublicKeyCredential)) {
        return { success: false, error: '인증기 생성에 실패했습니다.' };
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      // 3. Credential을 Base64로 변환
      const credentialId = this.arrayBufferToBase64Url(credential.rawId);
      const clientDataJSON = this.arrayBufferToBase64Url(response.clientDataJSON);
      const attestationObject = this.arrayBufferToBase64Url(response.attestationObject);
      const transports = response.getTransports ? response.getTransports() : undefined;

      // 4. 회원가입 완료
      const finishResponse = await apiService.webauthnRegisterFinish({
        challenge: challengeData.challenge,
        credential: {
          id: credentialId,
          rawId: credentialId,
          response: {
            clientDataJSON,
            attestationObject,
            transports
          },
          type: credential.type,
          authenticatorAttachment: credential.authenticatorAttachment || undefined
        }
      });

      if (finishResponse.success) {
        return { 
          success: true, 
          message: finishResponse.message || 'WebAuthn 회원가입이 완료되었습니다.' 
        };
      } else {
        return {
          success: false,
          error: getUnifiedApiErrorMessage(finishResponse)
        };
      }
    } catch (error: unknown) {
      console.error('WebAuthn 회원가입 오류:', error);
      const err = error as { name?: string; message?: string };
      let errorMessage = 'WebAuthn 회원가입 중 오류가 발생했습니다.';

      if (err.name === 'NotAllowedError') {
        errorMessage = '인증이 취소되었거나 시간이 초과되었습니다.';
      } else if (err.name === 'InvalidStateError') {
        errorMessage = '이미 등록된 인증기입니다.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = '이 브라우저는 WebAuthn을 지원하지 않습니다.';
      } else {
        errorMessage = getApiFailureMessage(error, err.message || errorMessage);
      }

      return { success: false, error: errorMessage };
    }
  }

  // WebAuthn 로그인
  async webauthnLogin(username: string): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      // 1. 로그인 시작 (Challenge 받기)
      const startResponse = await apiService.webauthnLoginStart({ username });

      if (!startResponse.success || !startResponse.data) {
        return {
          success: false,
          error: getUnifiedApiErrorMessage(startResponse)
        };
      }

      const challengeData = startResponse.data;

      // 2. 브라우저에서 Credential 가져오기
      // 크로스 디바이스 인증을 위해 allowCredentials를 설정하되,
      // transports에 "hybrid"를 포함하여 QR 코드 인증을 허용
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: this.base64UrlToArrayBuffer(challengeData.challenge),
        timeout: challengeData.timeout,
        rpId: challengeData.rpId,
        allowCredentials: challengeData.allowCredentials ? challengeData.allowCredentials.map(cred => {
          const transports = cred.transports || [];
          // 크로스 디바이스 인증을 위해 "hybrid" transport 추가
          const enhancedTransports: AuthenticatorTransport[] = [...transports] as AuthenticatorTransport[];
          if (!enhancedTransports.includes('hybrid' as AuthenticatorTransport)) {
            enhancedTransports.push('hybrid' as AuthenticatorTransport);
          }
          return {
            id: this.base64UrlToArrayBuffer(cred.id),
            type: cred.type as PublicKeyCredentialType,
            transports: enhancedTransports
          };
        }) : undefined, // allowCredentials가 없으면 모든 credential 허용 (크로스 디바이스 인증 가능)
        userVerification: challengeData.userVerification as UserVerificationRequirement
      };

      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      }) as PublicKeyCredential | null;

      if (!credential || !(credential instanceof PublicKeyCredential)) {
        return { success: false, error: '인증에 실패했습니다.' };
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      // 3. Credential을 Base64로 변환
      const credentialId = this.arrayBufferToBase64Url(credential.rawId);
      const clientDataJSON = this.arrayBufferToBase64Url(response.clientDataJSON);
      const authenticatorData = this.arrayBufferToBase64Url(response.authenticatorData);
      const signature = this.arrayBufferToBase64Url(response.signature);
      const userHandle = response.userHandle ? this.arrayBufferToBase64Url(response.userHandle) : undefined;

      // 4. 로그인 완료
      const finishResponse = await apiService.webauthnLoginFinish({
        challenge: challengeData.challenge,
        credential: {
          id: credentialId,
          rawId: credentialId,
          response: {
            clientDataJSON,
            authenticatorData,
            signature,
            userHandle
          },
          type: credential.type
        },
        deviceInfo: navigator.userAgent
      });

      if (finishResponse.success && finishResponse.data) {
        this.currentUser = finishResponse.data.user;
        return { success: true, user: finishResponse.data.user };
      } else {
        return {
          success: false,
          error: getUnifiedApiErrorMessage(finishResponse)
        };
      }
    } catch (error: unknown) {
      console.error('WebAuthn 로그인 오류:', error);
      const err = error as { name?: string; message?: string };
      let errorMessage = 'WebAuthn 로그인 중 오류가 발생했습니다.';

      if (err.name === 'NotAllowedError') {
        errorMessage = '인증이 취소되었거나 시간이 초과되었습니다.';
      } else if (err.name === 'InvalidStateError') {
        errorMessage = '인증기가 등록되지 않았습니다.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = '이 브라우저는 WebAuthn을 지원하지 않습니다.';
      } else {
        errorMessage = getApiFailureMessage(error, err.message || errorMessage);
      }

      return { success: false, error: errorMessage };
    }
  }

  // 유틸리티 메서드
  public base64UrlToArrayBuffer(base64Url: string): ArrayBuffer {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  public arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private getDeviceName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      return 'iOS Device';
    } else if (ua.includes('Android')) {
      return 'Android Device';
    } else if (ua.includes('Windows')) {
      return 'Windows';
    } else if (ua.includes('Mac')) {
      return 'macOS';
    } else if (ua.includes('Linux')) {
      return 'Linux';
    }
    return 'Unknown Device';
  }
}

// 싱글톤 인스턴스 생성
export const authService = new AuthService();
export default authService;
